// src/services/financialIntegrityService.js
// Updated: Monday, July 27, 2026 at 7:46 PM EDT

import * as XLSX from 'xlsx';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export async function runFinancialIntegrityScan(churchId) {
  if (!churchId) throw new Error('A church must be selected before running the scan.');
  const callable = httpsCallable(functions, 'scanFinancialIntegrity', {
    timeout: 540000,
  });
  const response = await callable({ churchId });
  return response.data;
}

function safeFileName(value) {
  return String(value || 'Church')
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '') || 'Church';
}

export function downloadFinancialIntegrityReport(scanResult, churchName) {
  if (!scanResult?.funds) throw new Error('Run a financial integrity scan before downloading a report.');

  const workbook = XLSX.utils.book_new();
  const summaryRows = [
    ['Financial Integrity Scan'],
    ['Church ID', scanResult.churchId],
    ['Church Name', churchName || ''],
    ['Scanned At', scanResult.scannedAt],
    ['Funds', scanResult.summary?.fundCount || 0],
    ['Transactions', scanResult.summary?.transactionCount || 0],
    ['Expenses', scanResult.summary?.expenseCount || 0],
    ['Batches', scanResult.summary?.batchCount || 0],
    ['Funds With Differences', scanResult.summary?.fundsWithDifferences || 0],
    ['Critical Findings', scanResult.summary?.criticalFindings || 0],
    ['Warnings', scanResult.summary?.warningFindings || 0],
    ['Informational Findings', scanResult.summary?.infoFindings || 0],
    ['Total Expected Balance', scanResult.summary?.totalExpectedBalance || 0],
    ['Total Stored Balance', scanResult.summary?.totalStoredBalance || 0],
    ['Total Stored Current Balance', scanResult.summary?.totalStoredCurrentBalance || 0],
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');

  const fundRows = scanResult.funds.map((fund) => ({
    'Fund ID': fund.fundId,
    'Fund Name': fund.fundName,
    'Opening Balance': fund.openingBalance,
    'Posted Transaction Activity': fund.postedTransactionActivity,
    'Approved Expense Activity': fund.approvedExpenseActivity,
    'Expected Balance': fund.expectedBalance,
    'Stored balance': fund.storedBalance,
    'Stored currentBalance': fund.storedCurrentBalance,
    'Difference vs balance': fund.differenceFromBalance,
    'Difference vs currentBalance': fund.differenceFromCurrentBalance,
    'Posted Transactions': fund.postedTransactionCount,
    'Approved Expenses': fund.approvedExpenseCount,
    'Pending/Draft Transactions': fund.pendingTransactionCount,
    'Pending/Draft Expenses': fund.pendingExpenseCount,
    'Unknown Status Records': fund.unknownStatusCount,
    'Has Difference': fund.hasDifference ? 'Yes' : 'No',
  }));
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(fundRows), 'Fund_Balances');

  const findingRows = (scanResult.findings || []).map((finding) => ({
    Severity: finding.severity,
    Code: finding.code,
    Collection: finding.collection || '',
    'Document ID': finding.documentId || '',
    'Fund ID': finding.fundId || '',
    Message: finding.message,
    Details: finding.details ? JSON.stringify(finding.details) : '',
  }));
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(findingRows.length ? findingRows : [{ Message: 'No findings.' }]),
    'Findings',
  );

  const statusRows = Object.entries(scanResult.statusCounts || {}).flatMap(([collectionName, statuses]) =>
    Object.entries(statuses).map(([status, count]) => ({
      Collection: collectionName,
      Status: status,
      Count: count,
    })),
  );
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(statusRows.length ? statusRows : [{ Collection: '', Status: '', Count: 0 }]),
    'Status_Counts',
  );

  const datePart = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(
    workbook,
    `${safeFileName(churchName)}_Financial_Integrity_Scan_${datePart}.xlsx`,
  );
}
